/**
 * H1 — itemised pay document from a determination.
 *
 * Fair Work regs 3.46(1)(g): itemise every loading, allowance and penalty.
 * regs 3.47–3.48: family/domestic-violence leave is recorded internally and
 * omitted from the worker-facing slip.
 */

export const ITEMISED_PAY_DOCUMENT_VERSION = '1.1.0';

export type PayDocumentLineKind =
  | 'ORDINARY'
  | 'LOADING'
  | 'PENALTY'
  | 'ALLOWANCE'
  | 'OVERTIME'
  | 'LEAVE'
  | 'DEDUCTION'
  | 'SUPER'
  | 'TAX';

export type PayDeterminationLine = {
  kind: PayDocumentLineKind;
  code: string;
  description: string;
  units?: number | null;
  rate?: number | null;
  amountCents: number;
};

export type ItemisedPayDocument = {
  version: string;
  slipLines: PayDeterminationLine[];
  internalLines: PayDeterminationLine[];
  slipGrossCents: number;
  internalGrossCents: number;
  fdvOmittedFromSlip: boolean;
};

const FDV_CODES = new Set([
  'FAMILY_DOMESTIC_VIOLENCE',
  'FAMILY_DOMESTIC_VIOLENCE_LEAVE',
  'FAMILY_AND_DOMESTIC_VIOLENCE',
  'FAMILY_AND_DOMESTIC_VIOLENCE_LEAVE',
  'PAID_FAMILY_AND_DOMESTIC_VIOLENCE_LEAVE',
  'PAID_FAMILY_DOMESTIC_VIOLENCE_LEAVE',
  'DOMESTIC_VIOLENCE',
  'DOMESTIC_VIOLENCE_LEAVE',
  'FDV',
  'FDV_LEAVE',
]);

const FDV_TEXT = /FAMILY(?:[\s_-]*AND)?[\s_-]*DOMESTIC[\s_-]*VIOLENCE|\bFDV\b/;

export function isFamilyDomesticViolencePayLine(line: PayDeterminationLine): boolean {
  const token = line.code
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (FDV_CODES.has(token)) return true;
  if (FDV_TEXT.test(token)) return true;
  return FDV_TEXT.test(line.description.trim().toUpperCase());
}

function sumCents(lines: readonly PayDeterminationLine[]): number {
  return lines.reduce((total, line) => total + line.amountCents, 0);
}

export type PayDocumentSourceLine = {
  lineType: string;
  displayName: string;
  amount: number;
  units?: number | null;
  rate?: number | null;
  providerLineId?: string | null;
  isEmployerLiability?: boolean;
  isEmployeeVisible?: boolean;
};

const SKIPPED_LINE_TYPES = new Set(['NOTE', 'NET_PAY', 'EMPLOYER_LIABILITY']);

function dollarsToCents(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

function lineCode(line: PayDocumentSourceLine): string {
  const fromProvider = line.providerLineId?.trim();
  if (fromProvider) return fromProvider.replace(/[\s-]+/g, '_').toUpperCase();
  return (
    line.displayName
      .trim()
      .replace(/[\s-]+/g, '_')
      .toUpperCase() || line.lineType.toUpperCase()
  );
}

export function kindFromPayDocumentLineType(
  lineType: string,
  displayName: string,
): PayDocumentLineKind {
  const type = lineType.trim().toUpperCase();
  if (type === 'ALLOWANCE') return 'ALLOWANCE';
  if (type === 'OVERTIME') return 'OVERTIME';
  if (type === 'LEAVE') return 'LEAVE';
  if (type === 'DEDUCTION' || type === 'SALARY_SACRIFICE') return 'DEDUCTION';
  if (type === 'TAX') return 'TAX';
  if (type === 'SUPER' || type === 'EMPLOYEE_SUPER') return 'SUPER';
  const name = displayName.toUpperCase();
  if (name.includes('LOADING')) return 'LOADING';
  if (/PENALTY|SATURDAY|SUNDAY|PUBLIC.?HOLIDAY/.test(name)) return 'PENALTY';
  return 'ORDINARY';
}

export function determinationLinesFromPayDocumentLines(
  lines: readonly PayDocumentSourceLine[],
): PayDeterminationLine[] {
  const determination: PayDeterminationLine[] = [];
  for (const line of lines) {
    const type = line.lineType.trim().toUpperCase();
    if (SKIPPED_LINE_TYPES.has(type) || line.isEmployerLiability) continue;
    const amountCents = dollarsToCents(line.amount);
    if (amountCents === null) continue;
    determination.push({
      kind: kindFromPayDocumentLineType(line.lineType, line.displayName),
      code: lineCode(line),
      description: line.displayName.trim() || type,
      units: line.units ?? null,
      rate: dollarsToCents(line.rate),
      amountCents,
    });
  }
  return determination;
}

export function renderItemisedPayDocument(
  determination: readonly PayDeterminationLine[],
): ItemisedPayDocument {
  const internalLines = determination.map((line) => ({ ...line }));
  const slipLines = internalLines.map((line) =>
    isFamilyDomesticViolencePayLine(line)
      ? {
          ...line,
          // Fair Work Regulations 3.48(2): default to payment for work.
          // A leave label requires an employee request, which this input lacks.
          kind: 'ORDINARY' as const,
          code: 'ORDINARY',
          description: 'Ordinary hours',
        }
      : line,
  );
  return {
    version: ITEMISED_PAY_DOCUMENT_VERSION,
    slipLines,
    internalLines,
    slipGrossCents: sumCents(slipLines),
    internalGrossCents: sumCents(internalLines),
    fdvOmittedFromSlip: determination.some(isFamilyDomesticViolencePayLine),
  };
}

export function itemisedRenderSnapshot(document: ItemisedPayDocument): Record<string, unknown> {
  return {
    version: document.version,
    source: 'statura-itemised-h1',
    protected_leave_omitted_from_slip: document.fdvOmittedFromSlip,
    slip_gross_cents: document.slipGrossCents,
    slip_lines: document.slipLines.map((line) => ({
      kind: line.kind,
      code: line.code,
      description: line.description,
      units: line.units ?? null,
      rate: line.rate ?? null,
      amount_cents: line.amountCents,
    })),
  };
}

export function payDocumentIdFromChecksum(checksumSha256: string): string {
  const hex = checksumSha256
    .replace(/[^0-9a-f]/gi, '')
    .padEnd(32, '0')
    .slice(0, 32);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
